import { useRef, useState } from 'react';
import type { PointerEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { PortalSignatureResult } from '@cloudsign/shared';
import type { SignatureStrokes } from '@cloudsign/shared';
import { api } from '../lib/api';
import { t } from '../lib/i18n';
import { Button } from '../components/ui';

const WIDTH = 400;
const HEIGHT = 160;
const MAX_STROKES = 200;
const MAX_POINTS = 5000;
const round = (n: number): number => Math.round(n * 10) / 10;

/**
 * Draws a signature with a finger, pen or mouse and saves it at the site (POST /visits/signatures). The answer is the
 * returned `{ fileId }`, the same shape a kiosk signature has. A signature is vectors (x, y, time), never an image.
 */
export function SignaturePad({
  label,
  siteId,
  value,
  error,
  onChange,
}: {
  label: string;
  siteId: string;
  value: { fileId: string } | undefined;
  error: string | undefined;
  onChange: (v: { fileId: string } | undefined) => void;
}) {
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const strokes = useRef<[number, number, number][][]>([]);
  const started = useRef(0);
  const drawing = useRef(false);
  const [count, setCount] = useState(0);
  const [failed, setFailed] = useState(false);

  const save = useMutation({
    mutationFn: (signature: SignatureStrokes) => api.post('/visits/signatures', { body: { siteId, signature }, schema: PortalSignatureResult }),
    onSuccess: (r) => {
      setFailed(false);
      onChange({ fileId: r.fileId });
    },
    onError: () => setFailed(true),
  });

  const point = (e: PointerEvent<HTMLCanvasElement>): [number, number, number] => {
    const rect = e.currentTarget.getBoundingClientRect();
    const sx = rect.width > 0 ? WIDTH / rect.width : 1;
    const sy = rect.height > 0 ? HEIGHT / rect.height : 1;
    return [round((e.clientX - rect.left) * sx), round((e.clientY - rect.top) * sy), Math.max(0, Math.round(performance.now() - started.current))];
  };
  // jsdom (tests) has no 2D context; drawing then only records the strokes.
  const ctx = () => {
    try {
      return canvas.current?.getContext('2d') ?? null;
    } catch {
      return null;
    }
  };

  const down = (e: PointerEvent<HTMLCanvasElement>) => {
    if (strokes.current.length >= MAX_STROKES) return;
    if (strokes.current.length === 0) started.current = performance.now();
    drawing.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const p = point(e);
    strokes.current.push([p]);
    setCount(strokes.current.length);
    const c = ctx();
    if (c) {
      c.beginPath();
      c.moveTo(p[0], p[1]);
    }
  };
  const move = (e: PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const s = strokes.current[strokes.current.length - 1];
    if (!s || s.length >= MAX_POINTS) return;
    const p = point(e);
    s.push(p);
    const c = ctx();
    if (c) {
      c.lineTo(p[0], p[1]);
      c.stroke();
    }
  };
  const up = () => {
    drawing.current = false;
  };
  const clear = () => {
    strokes.current = [];
    setCount(0);
    setFailed(false);
    onChange(undefined);
    ctx()?.clearRect(0, 0, WIDTH, HEIGHT);
  };

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-slate-800">{label}</p>
      {value ? (
        <p className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2 text-sm">
          <span>{t('fieldInput.signatureSaved')}</span>
          <button type="button" className="text-brand underline" onClick={clear}>
            {t('fieldInput.change')}
          </button>
        </p>
      ) : (
        <>
          <canvas
            ref={canvas}
            width={WIDTH}
            height={HEIGHT}
            aria-label={t('fieldInput.signaturePad', { label })}
            className="w-full max-w-md touch-none rounded-md border border-slate-300 bg-white"
            onPointerDown={down}
            onPointerMove={move}
            onPointerUp={up}
            onPointerCancel={up}
            onPointerLeave={up}
          />
          <div className="flex gap-2">
            <Button type="button" disabled={count === 0 || save.isPending} onClick={() => save.mutate({ v: 1, width: WIDTH, height: HEIGHT, strokes: strokes.current })}>
              {t('fieldInput.signatureSave')}
            </Button>
            <Button type="button" variant="secondary" disabled={count === 0} onClick={clear}>
              {t('fieldInput.signatureClear')}
            </Button>
          </div>
        </>
      )}
      {failed ? <p className="text-sm text-red-700">{t('fieldInput.signatureFailed')}</p> : null}
      {error ? <p className="text-sm text-red-700">{error}</p> : null}
    </div>
  );
}
